import { Component } from '@theme/component';

/**
 * Each `_collection-tab` block renders a pill+panel pair together (so its `collection`
 * setting resolves correctly server-side — see _collection-tab.liquid). On connect, this
 * moves every pill into the shared pills row so they display as a row above the panel
 * stack. Panel switching just toggles `hidden` on pre-rendered panels — no re-fetching.
 *
 * NOTE: the `requiredRefs` entries deliberately omit the `[]` suffix that the markup uses
 * (`ref="pills[]"`). Component's #updateRefs() strips `[]` when it stores an array ref
 * (`path = isArray ? refName.slice(0, -2) : refName`) but checks `requiredRefs` against the
 * raw, unstripped string — so `'pills[]'` here would always throw MissingRefError.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} pillsContainer
 * @property {HTMLElement} panelsContainer
 * @property {HTMLButtonElement[]} pills
 * @property {HTMLElement[]} panels
 * @extends {Component<Refs>}
 */
class TabbedCollections extends Component {
  requiredRefs = ['pillsContainer', 'panelsContainer', 'pills', 'panels'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // Move each pill (originally rendered next to its own panel) into the dedicated
    // pills row. Panels stay where they are, stacked in panelsContainer.
    for (const pill of this.refs.pills) {
      this.refs.pillsContainer.appendChild(pill);
    }

    // Defer the active panel's images until the section is nearly on screen. See
    // #hydrateImages for why this cannot be left to `loading="lazy"` alone.
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#hydrateImages(this.refs.panels.find((panel) => !panel.hidden));
      },
      { rootMargin: '600px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Start loading a panel's images, once per panel.
   *
   * The markup ships `loading="lazy"` so nothing downloads for tabs the visitor never
   * opens — that alone was worth ~32 wasted image requests on the homepage. But lazy
   * loading CANNOT be relied on to start them again: panels are hidden with the `hidden`
   * attribute (see selectTab), images inside a `display: none` subtree are never evaluated,
   * and revealing the subtree does not reliably re-trigger evaluation. Measured on this
   * section: the active panel sat at 0/8 loaded with the section fully in the viewport, and
   * clicking a pill gave 0/8 on the panel it revealed. Flipping the attribute to `eager`
   * starts the fetch immediately, so hydration is done explicitly here instead.
   *
   * @param {HTMLElement | undefined} panel
   */
  #hydrateImages(panel) {
    if (!panel || panel.dataset.imagesHydrated) return;
    panel.dataset.imagesHydrated = 'true';

    for (const image of panel.querySelectorAll('img[loading="lazy"]')) {
      image.loading = 'eager';
    }
  }

  /**
   * @param {MouseEvent & {target: HTMLElement}} event
   */
  selectTab(event) {
    const button = /** @type {HTMLElement | null} */ (event.target.closest('button'));
    if (!button) return;

    const index = this.refs.pills.indexOf(/** @type {HTMLButtonElement} */ (button));
    if (index === -1) return;

    this.refs.pills.forEach((pill, i) => {
      const isActive = i === index;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.refs.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    // The revealed panel's images are still lazy and will not start on their own.
    this.#hydrateImages(this.refs.panels[index]);
  }
}

if (!customElements.get('tabbed-collections-component')) {
  customElements.define('tabbed-collections-component', TabbedCollections);
}
